import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Calendar } from "primereact/calendar";
import { addCoupon, updateCoupon, getSpecificCoupon, getCoupons } from "../../store/AsyncMethods/CouponMethod";
import { SET_SPECIFIC_COUPON } from "../../store/Types/CouponTypes";
import InputFocus from "../../hooks/InputFocus";

export default function AddCoupon() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { couponId } = useParams();
  const { user } = useSelector((state) => state.AuthReducer);
  const { specificCoupon } = useSelector((state) => state.CouponReducer);
  const inputRef = InputFocus();

  const isEdit = !!couponId;

  useEffect(() => {
    if (isEdit) {
      dispatch(getSpecificCoupon(couponId));
    }
    return () => {
      dispatch({ type: SET_SPECIFIC_COUPON, payLoad: null });
    };
  }, [couponId]);

  useEffect(() => {
    if (isEdit && specificCoupon) {
      formik.setValues({
        code: specificCoupon.code || "",
        discount_type: specificCoupon.discount_type || "percentage",
        discount_value: parseFloat(specificCoupon.discount_value) || 0,
        min_order_amount: parseFloat(specificCoupon.min_order_amount) || 0,
        max_discount_amount: specificCoupon.max_discount_amount ? parseFloat(specificCoupon.max_discount_amount) : null,
        usage_limit: specificCoupon.usage_limit || null,
        expiry_date: specificCoupon.expiry_date ? new Date(specificCoupon.expiry_date) : null,
        is_active: specificCoupon.is_active === 1 || specificCoupon.is_active === true ? 1 : 0,
      });
    }
  }, [specificCoupon]);

  const discountTypeOptions = [
    { label: "Percentage (%)", value: "percentage" },
    { label: "Fixed Amount (PKR)", value: "fixed" },
  ];

  const statusOptions = [
    { name: "Active", value: 1 },
    { name: "Inactive", value: 0 },
  ];

  const formik = useFormik({
    initialValues: {
      code: "",
      discount_type: "percentage",
      discount_value: 0,
      min_order_amount: 0,
      max_discount_amount: null,
      usage_limit: null,
      expiry_date: null,
      is_active: 1,
    },
    validationSchema: Yup.object({
      code: Yup.string().required("Coupon code is required").min(3, "Min 3 characters"),
      discount_type: Yup.string().required("Discount type is required"),
      discount_value: Yup.number().required("Discount value is required").min(0.01, "Must be greater than 0"),
    }),
    onSubmit: async (values) => {
      const payload = {
        ...values,
        code: values.code.trim().toUpperCase(),
        expiry_date: values.expiry_date
          ? new Date(values.expiry_date).toISOString().split("T")[0]
          : null,
        max_discount_amount: values.max_discount_amount || null,
        usage_limit: values.usage_limit || null,
      };

      let success;
      if (isEdit) {
        success = await dispatch(updateCoupon(payload, couponId));
      } else {
        success = await dispatch(addCoupon(payload));
      }

      if (success) {
        dispatch(getCoupons());
        navigate("/manage-coupons");
      }
    },
  });

  return (
    <div className="mx-4">
      <div>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-black-btn"
          onClick={() => navigate(-1)}
        />
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
              {isEdit ? "Edit Coupon" : "Add Coupon"}
            </span>
          </Divider>

          <div className="px-4 gap-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {/* Code */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="code">Coupon Code *</label>
                <InputText
                  ref={inputRef}
                  id="code"
                  name="code"
                  className="w-full p-primary-input uppercase"
                  value={formik.values.code}
                  onChange={(e) => formik.setFieldValue("code", e.target.value.toUpperCase())}
                  placeholder="e.g. SAVE20"
                />
              </div>
              {formik.touched.code && formik.errors.code && (
                <div className="p-error">{formik.errors.code}</div>
              )}
            </div>

            {/* Discount Type */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="discount_type">Discount Type *</label>
                <Dropdown
                  id="discount_type"
                  name="discount_type"
                  options={discountTypeOptions}
                  value={formik.values.discount_type}
                  onChange={(e) => formik.setFieldValue("discount_type", e.value)}
                  className="w-full"
                />
              </div>
              {formik.touched.discount_type && formik.errors.discount_type && (
                <div className="p-error">{formik.errors.discount_type}</div>
              )}
            </div>

            {/* Discount Value */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="discount_value">
                  Discount Value * {formik.values.discount_type === "percentage" ? "(%)" : "(PKR)"}
                </label>
                <InputNumber
                  id="discount_value"
                  name="discount_value"
                  className="w-full"
                  inputClassName="p-primary-input w-full"
                  value={formik.values.discount_value}
                  onValueChange={(e) => formik.setFieldValue("discount_value", e.value)}
                  min={0}
                  max={formik.values.discount_type === "percentage" ? 100 : undefined}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                />
              </div>
              {formik.touched.discount_value && formik.errors.discount_value && (
                <div className="p-error">{formik.errors.discount_value}</div>
              )}
            </div>

            {/* Min Order Amount */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="min_order_amount">Min Order Amount (PKR)</label>
                <InputNumber
                  id="min_order_amount"
                  className="w-full"
                  inputClassName="p-primary-input w-full"
                  value={formik.values.min_order_amount}
                  onValueChange={(e) => formik.setFieldValue("min_order_amount", e.value || 0)}
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                />
              </div>
            </div>

            {/* Max Discount (only for percentage) */}
            {formik.values.discount_type === "percentage" && (
              <div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="max_discount_amount">Max Discount Cap (PKR) — optional</label>
                  <InputNumber
                    id="max_discount_amount"
                    className="w-full"
                    inputClassName="p-primary-input w-full"
                    value={formik.values.max_discount_amount}
                    onValueChange={(e) => formik.setFieldValue("max_discount_amount", e.value || null)}
                    min={0}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    placeholder="No cap"
                  />
                </div>
              </div>
            )}

            {/* Usage Limit */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="usage_limit">Usage Limit — optional (blank = unlimited)</label>
                <InputNumber
                  id="usage_limit"
                  className="w-full"
                  inputClassName="p-primary-input w-full"
                  value={formik.values.usage_limit}
                  onValueChange={(e) => formik.setFieldValue("usage_limit", e.value || null)}
                  min={1}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            {/* Expiry Date */}
            <div>
              <div className="flex flex-col gap-2">
                <label htmlFor="expiry_date">Expiry Date — optional</label>
                <Calendar
                  id="expiry_date"
                  className="w-full"
                  inputClassName="p-primary-input w-full"
                  value={formik.values.expiry_date}
                  onChange={(e) => formik.setFieldValue("expiry_date", e.value)}
                  minDate={new Date()}
                  dateFormat="dd/mm/yy"
                  showIcon
                  placeholder="No expiry"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <div className="flex flex-col gap-2">
                <label>Status</label>
                <SelectButton
                  name="is_active"
                  options={statusOptions}
                  optionLabel="name"
                  optionValue="value"
                  className="flex"
                  pt={{
                    root: { className: "flex" },
                    button: ({ context }) => ({
                      className: context.selected
                        ? "p-primary-highlight-btn w-full text-lg text-center"
                        : "w-full text-lg text-center",
                    }),
                  }}
                  value={formik.values.is_active}
                  onChange={(e) => formik.setFieldValue("is_active", e.value)}
                />
              </div>
            </div>
          </div>

          <div className="mt-16">
            <div className="flex justify-end gap-4">
              <Button
                label="Clear"
                icon="pi pi-times"
                className="p-red-btn"
                type="button"
                onClick={() => formik.resetForm()}
              />
              <Button
                label={isEdit ? "Update" : "Submit"}
                icon="pi pi-check"
                className="p-secondary-btn"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
